import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Grid, MenuItem } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import globalStyles from "../../../../assets/styles/global.module.scss";
import styles from "../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss";
import formStyles from "../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/whatsAppTemplateForm.module.scss";

import IconsColor from "../../../utility/iconsColor";
import TouchTooltip from "../utils/TouchTooltip";
import { StyledMenu } from "../utils/StyledMenu";
import { getButtonsIconByName, isAllowedCarouselBtnType, isAllowedCarouselSubBtnName, isTemplateCreated } from "../constant";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";
import { useCarouselFunctions } from "../utils/useCarouselFunctions";
import { formatString, toCapitalizedCase } from "../../../../utils/constants";

const CarouselTemplateInteractiveActionsPreview = (props: any) => {
    const { editable = false, getMenuDisableFlag, calculateMenuPosition } = props;

    const createTemplatePayload = useTemplateLibraryStore((state) => state.createTemplatePayload);
    const whatsappTemplateButtonGenericData = useTemplateLibraryStore((state) => state.whatsappTemplateButtonGenericData);

    const { checkDisableAddBtnForCarousel, handleAddActionsButtonForCarousel } = useCarouselFunctions();

    const [addButtonAnchorEl, setAddButtonAnchorEl] = useState<HTMLElement | null>(null);
    const [menuPosition, setMenuPosition] = useState<"top" | "bottom">("bottom");

    // Constants
    const isAddDisabled = isTemplateCreated(createTemplatePayload) || checkDisableAddBtnForCarousel();
    const openMenuOptions = Boolean(addButtonAnchorEl);

    const filteredBtnOptions = whatsappTemplateButtonGenericData?.filter((btn: any) =>
        isAllowedCarouselBtnType(btn?.whatsapp_template_button_type_name)
    ) ?? [];

    // Functions
    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (isAddDisabled) return;
        if (calculateMenuPosition) {
            setMenuPosition(calculateMenuPosition(e.currentTarget));
        }
        setAddButtonAnchorEl(e.currentTarget);
    };

    const handleCloseMenu = () => setAddButtonAnchorEl(null);

    const handleSelectButton = (buttonTypeId: number, subTypeId: number) => {
        handleAddActionsButtonForCarousel(buttonTypeId, subTypeId);
        handleCloseMenu();
    };

    const renderStyledMenu = () => (
        <StyledMenu
            id="carousel-interactive-actions-menu"
            MenuListProps={{
                "aria-labelledby": "carousel-interactive-actions-button",
                className: formStyles.formPopoverMenuList,
            }}
            anchorEl={addButtonAnchorEl}
            open={openMenuOptions}
            onClose={handleCloseMenu}
            position={menuPosition}
        >
            {filteredBtnOptions?.map((btnOption: any, index: number) => (
                <React.Fragment key={btnOption?.whatsapp_template_button_type_id ?? index}>
                    <div className={formStyles.formPopoverTitle}>
                        {toCapitalizedCase(formatString(btnOption?.whatsapp_template_button_type_name))}
                    </div>

                    {btnOption?.whatsapp_template_button_sub_type_data
                        ?.filter((subType: any) => isAllowedCarouselSubBtnName(subType?.whatsapp_template_button_sub_type_name))
                        ?.map((subType: any) => (
                            <MenuItem
                                key={subType?.whatsapp_template_button_sub_type_id}
                                disabled={getMenuDisableFlag?.(btnOption?.whatsapp_template_button_type_id, subType, true)}
                                disableRipple
                                onClick={() => handleSelectButton(btnOption?.whatsapp_template_button_type_id, subType?.whatsapp_template_button_sub_type_id)}
                            >
                                <Grid container alignItems="center" className={formStyles.formPopoverItemRow}>
                                    <Grid item>
                                        {getButtonsIconByName(toCapitalizedCase(formatString(subType?.whatsapp_template_button_sub_type_name)))}
                                    </Grid>

                                    <Grid item>
                                        <Grid container direction="column" className={formStyles.formPopoverItemColumn}>
                                            <Grid item className={formStyles.formPopoverItemLabel}>
                                                {toCapitalizedCase(formatString(subType?.whatsapp_template_button_sub_type_name))}
                                            </Grid>

                                            {getMenuDisableFlag?.(btnOption?.whatsapp_template_button_type_id, subType, true) && (
                                                <Grid item className={formStyles.formPopoverItemDesc}>
                                                    {subType?.whatsapp_template_button_sub_type_description}
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                        ))}

                    {index !== filteredBtnOptions.length - 1 && (
                        <Grid item className={formStyles.formPopoverDivider}>
                            <Divider />
                        </Grid>
                    )}
                </React.Fragment>
            ))}
        </StyledMenu>
    );

    const renderAddButton = () => {
        const addButton = (
            <Button
                id="carousel-interactive-actions-button"
                variant="contained"
                disableElevation
                onClick={handleOpenMenu}
                className={styles.addButton}
                disabled={isAddDisabled}
                style={{ opacity: isAddDisabled ? 0.5 : 1, width: "100%", maxWidth: "100%" }}
                startIcon={<IconsColor iconName="plus" width="12px" height="12px" fillColor="#1B7D4E" />}
                endIcon={<ArrowDropDownIcon sx={{ width: "22px !important", height: "22px !important" }} />}
            >
                Add Button
            </Button>
        );

        if (isTemplateCreated(createTemplatePayload)) {
            return (
                <TouchTooltip title="New buttons cannot be added once the template has been created." >
                    <span className={globalStyles.clickable}>
                        {addButton}
                    </span>
                </TouchTooltip>
            );
        }

        return addButton;
    };

    useEffect(() => {
        if (!editable) {
            setAddButtonAnchorEl(null);
        }
    }, [editable]);

    if (!editable) {
        return null;
    }

    return (
        <Box className={`${globalStyles.marginTop10px} ${styles.buttonsActionsDisabledCursor}`}>
            {renderAddButton()}
            {renderStyledMenu()}
        </Box>
    );
};

export default CarouselTemplateInteractiveActionsPreview;